import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

interface ExamPrepEntry {
  name: string;
  when: string;
}

/**
 * @customElement chancen-component-choose-intensive-courses
 *
 * Shows boxes to choose the IntensiveCourses for a Student based on some possible courses.
 *
 * **Possible attrubutes:**
 *
 * - `examPrepEntries: ExamPrepEntry[]` - The courses to choose from
 * - `student: Student` - The student to mutate
 * - `deactivateLock: boolean` - If the lock should be deactivated
 * - `maximumChoosen: number` - The maximum amount of courses that can be choosen
 *
 * **possible events:**
 *
 * - `value-picked: ValueEvent<Student>` - Emitted when the Student was changed
 */
@customElement('chancen-component-choose-exam-prep')
export class ChancenComponentChooseExamPrep extends ChemistryLitElement {
  // ! Use of hasChanged comes from a problem in the registration formular.
  // ! This should be removed when the correct refactoring is done.
  @property({ attribute: false, hasChanged: () => true }) student?: Student;

  @property({ attribute: false }) examPrepEntries: ExamPrepEntry[] = [
    {
      name: 'Prüfungsvorbereitung Mathe',
      when: 'Frühjahr 2025: 4 Samstage vor dem Prüfungstermin, jeweils 3h. Termine werden noch bekannt gegeben.',
    },
    {
      name: 'Prüfungsvorbereitung Deutsch',
      when: 'Frühjahr 2025: 4 Samstage vor dem Prüfungstermin, jeweils 3h. Termine werden noch bekannt gegeben.',
    },
    {
      name: 'Prüfungsvorbereitung Englisch',
      when: 'Frühjahr 2025: 4 Samstage vor dem Prüfungstermin, jeweils 3h. Termine werden noch bekannt gegeben.',
    },
  ];
  @property({ attribute: false }) deactivateLock = false;
  @property({ attribute: false }) maximumChoosen = 3;

  private _studentField() {
    if (!this.student) {
      return [];
    }
    return (this.student.examPreperationChoosen ??= []);
  }

  private _emitStudentChanged() {
    const event = new CustomEvent('value-picked', {
      detail: {
        value: this.student,
      },
    });
    this.dispatchEvent(event);
  }

  private _findIndex(str: string) {
    return this._studentField().findIndex(e => e === str);
  }

  private _onChoiceClick(deactivated: boolean, str: string) {
    if (deactivated) return;

    const index = this._findIndex(str);
    if (index === undefined || index === -1) {
      this._studentField().push(str);
    } else {
      this._studentField().splice(index, 1);
    }

    this._emitStudentChanged();
    this.requestUpdate();
  }

  private _checkDeactivated(choosen: boolean, str: string): boolean {
    if (this.deactivateLock) return false;
    if (!choosen) {
      if (this._studentField() && this._studentField().length >= this.maximumChoosen) {
        return true;
      }
    }

    return false;
  }

  private _choiceItem(deactivated: boolean, choosen: boolean, entry: ExamPrepEntry) {
    return html`
      <cr-basic-card
        ${css({
          boxShadow: 'none',
          cursor: deactivated ? 'not-allowed' : 'pointer',
          background: deactivated
            ? this.colors.neutral.shade(300)
            : choosen
            ? this.colors.primary.shade(100)
            : this.colors.neutral.shade(100),
        })}
        @click=${() => this._onChoiceClick(deactivated, entry.name)}
      >
        <cr-flex horizontal space="md" crossaxis-center>
          <cr-icon
            ${css({
              color: deactivated ? this.colors.neutral.shade(800) : choosen ? this.colors.primary : this.colors.neutral,
              fontSize: '1.8rem',
            })}
          >
            ${deactivated ? 'not_interested' : choosen ? 'done' : 'close'}
          </cr-icon>
          <cr-flex space="sm" ${css({ minWidth: '250px' })}>
            <cr-text bold> ${entry.name} </cr-text>
            <cr-text>${entry.when} </cr-text>
          </cr-flex>
        </cr-flex>
      </cr-basic-card>
    `;
  }

  override render() {
    return html` <cr-flex space="md">
      ${(this.examPrepEntries ?? []).length === 0
        ? html`<cr-basic-card ${css({ boxShadow: 'none', background: this.colors.tone.error.shade(100) })}>
            <cr-text>Leider stehen aktuell keine Termine zur Verfügung</cr-text>
          </cr-basic-card>`
        : nothing}
      ${repeat(
        this.examPrepEntries ?? [],
        c => c.name,
        c => {
          const choosen = this._findIndex(c.name) !== -1;
          return this._choiceItem(this._checkDeactivated(choosen, c.name), choosen, c);
        },
      )}
    </cr-flex>`;
  }
}
